import { gql } from '@apollo/client';

import {
  AscContributorProjectAllocationFragmentDocument,
  AscPeriodLockStatusFragmentDocument,
  BusinessQuestionsFragmentDocument,
  CompanyFragmentDocument,
  CursorPageInfoFragmentDocument,
  AccountingQreFragmentDocument,
  PayrollQreFragmentDocument,
  PayrollTokenFragmentDocument,
  AscProjectFragmentDocument,
  QuestionSectionFragmentDocument,
  SyncedProvidersFragmentDocument,
  UserFragmentDocument,
  FederalRdTaxCreditDocumentsFragmentDocument,
  StateRdTaxCreditDocumentFragmentDocument,
  MemberFragmentDocument,
  GetContributorFragmentDocument,
  GetRdExpenseFragmentDocument,
  GetAccountingExpenseFragmentDocument,
  GetPayrollExpenseFragmentDocument,
  ContributorFragmentDocument,
  CompanyInvitationFragmentDocument,
  OrganizationFragmentDocument,
  FileUploadFragmentDocument,
  BulkTicketingDataFileUploadFragmentDocument,
  SyncedPayrollMonthExpenseFragmentDocument,
  SyncedRdPayrollCostFragmentDocument,
  AscWageFragmentDocument,
  AscProjectTeamMemberFragmentDocument,
  ProjectRdDeterminationFragmentDocument,
  RdProjectFragmentDocument,
  TicketingUserFragmentDocument,
  QuestionFragmentDocument,
  RdProjectTpTicketsViewFragmentDocument,
  RdProjectTeamMemberFragmentDocument,
  AscProjectTpTicketsViewFragmentDocument,
  ProductFragmentDocument,
  FiscalYearFragmentDocument,
  ApplyWeightedRdPercentageToExpensesResultFragmentDocument,
  AscGroupingTicketOrCollectionFragmentDocument,
  RdGroupingTicketOrCollectionFragmentDocument,
  DocumentEventFragmentDocument,
  RdExpenseProjectAllocationFragmentDocument,
  ApplyWeightedCapitalizedPercentToContributorsResultFragmentDocument,
  GetRdTaxCreditContributorFragmentDocument,
  SredPayrollExpenseFragmentDocument,
  AscProjectApprovedStatusFragmentDocument,
  SredProjectFragmentDocument,
} from './fragments';

export const GET_USER = gql`
  query GetUser($userId: ID!) {
    getUser(userId: $userId) {
      ...User
    }
  }
  ${UserFragmentDocument}
  ${OrganizationFragmentDocument}
`;

export const GET_ORGANIZATION_MEMBERS_CSV = gql`
  query GetOrganizationMembersCsv($organizationId: ID!) {
    getOrganizationMembersCsv(organizationId: $organizationId) {
      columnNames
      resultCsv
      signedUrl
    }
  }
`;

export const GET_USER_FROM_AUTH = gql`
  query GetUserFromAuth($firebaseUserId: String!) {
    getUserFromAuth(firebaseUserId: $firebaseUserId) {
      userId
      isSuperUser
    }
  }
`;

// Provisions the DB user for a session authenticated in Firebase but not yet in our DB. The server
// verifies the caller's ID token and provisions from the verified uid — no args needed.
export const PROVISION_CURRENT_USER = gql`
  mutation ProvisionCurrentUser {
    provisionCurrentUser
  }
`;

export const RECONCILE_USER_STATE = gql`
  mutation ReconcileUserState($userId: ID!) {
    reconcileUserState(userId: $userId) {
      id
    }
  }
`;

export const REPORT_LOGIN_FAILURE = gql`
  mutation ReportLoginFailure($input: ReportLoginFailureInput!) {
    reportLoginFailure(input: $input)
  }
`;

export const UPDATE_COMPANY_QUESTIONS = gql`
  mutation UpdateCompanyQuestions($companyId: ID!, $questions: CompanyQuestionsInput!) {
    updateCompanyQuestions(companyId: $companyId, questions: $questions) {
      ...Company
      businessQuestions {
        ...BusinessQuestions
      }
      priorYearQREs {
        year
        amount
      }
      priorYearRevenues {
        id
        revenueCents
        year
        revenueSource
      }
    }
  }
  ${BusinessQuestionsFragmentDocument}
  ${CompanyFragmentDocument}
`;

export const GET_PRIOR_YEAR_REVENUES = gql`
  query GetPriorYearRevenues($companyId: ID!) {
    getCompany(companyId: $companyId) {
      id
      priorYearRevenues {
        id
        revenueCents
        year
        taxYearStartDate
        taxYearEndDate
        revenueSource
      }
    }
  }
`;

export const RECORD_DOWNLOAD_DOCUMENT = gql`
  mutation RecordDownloadDocument($fiscalYearId: ID!, $documentInput: DocumentInput!) {
    recordDownloadDocument(fiscalYearId: $fiscalYearId, documentInput: $documentInput) {
      id
    }
  }
`;

export const RECORD_SUBMIT_DOCUMENTS = gql`
  mutation RecordSubmitDocuments($fiscalYearId: ID!, $documentInputs: [DocumentInput!]!) {
    recordSubmitDocuments(fiscalYearId: $fiscalYearId, documentInputs: $documentInputs) {
      id
    }
  }
`;

export const GET_ASC_PROJECTS = gql`
  query GetAscProjects(
    $companyId: ID!
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $filter: AscProjectFilter! = {}
    $paging: CursorPaging! = { first: 100 }
    $sorting: [AscProjectSort!]! = []
  ) {
    getAscProjects(
      companyId: $companyId
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      filter: $filter
      paging: $paging
      sorting: $sorting
    ) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...AscProject
        }
        cursor
      }
    }
  }
  ${AscProjectFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_ASC_PROJECT = gql`
  query GetAscProject($ascProjectId: ID!, $fiscalYearId: ID!, $periodName: PeriodName!) {
    getAscProject(ascProjectId: $ascProjectId, fiscalYearId: $fiscalYearId, periodName: $periodName) {
      ...AscProject
    }
  }
  ${AscProjectFragmentDocument}
`;

export const GET_RD_PROJECT = gql`
  query GetRdProject($rdProjectId: ID!) {
    getRdProject(rdProjectId: $rdProjectId) {
      ...RdProject
    }
  }
  ${RdProjectFragmentDocument}
`;

export const GET_RD_PROJECTS = gql`
  query GetRdProjects(
    $fiscalYearId: ID!
    $filter: RdProjectFilter! = {}
    $paging: CursorPaging! = { first: 100 }
    $sorting: [RdProjectSort!]! = []
  ) {
    getRdProjects(fiscalYearId: $fiscalYearId, filter: $filter, paging: $paging, sorting: $sorting) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...RdProject
        }
        cursor
      }
    }
  }
  ${RdProjectFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_SRED_PROJECTS = gql`
  query GetSredProjects(
    $sredTaxCreditId: ID!
    $filter: SredProjectFilter! = {}
    $paging: CursorPaging! = { first: 100 }
    $sorting: [SredProjectSort!]! = []
  ) {
    getSredProjects(sredTaxCreditId: $sredTaxCreditId, filter: $filter, paging: $paging, sorting: $sorting) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...SredProject
        }
        cursor
      }
    }
  }
  ${SredProjectFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_RD_PROJECTS_SUMMARY = gql`
  query GetRdProjectsSummary($fiscalYearId: ID!, $filter: RdProjectsSummaryFilter) {
    getRdProjectsSummary(fiscalYearId: $fiscalYearId, filter: $filter) {
      count {
        name
      }
      avg {
        ticketCount
      }
    }
  }
`;

export const UPDATE_ASC_PROJECT = gql`
  mutation UpdateAscProject(
    $ascProjectId: ID!
    $updateAscProjectInput: UpdateAscProjectInput!
    $fiscalYearId: ID!
    $periodName: PeriodName!
  ) {
    updateAscProject(ascProjectId: $ascProjectId, updateAscProjectInput: $updateAscProjectInput) {
      ...AscProject
    }
  }
  ${AscProjectFragmentDocument}
`;

export const UPDATE_ASC_PROJECT_CAPITALIZATION = gql`
  mutation UpdateAscProjectCapitalization(
    $ascProjectId: ID!
    $updateAscProjectCapitalizationInput: UpdateAscProjectCapitalizationInput!
    $fiscalYearId: ID!
    $periodName: PeriodName!
  ) {
    updateAscProjectCapitalization(
      ascProjectId: $ascProjectId
      updateAscProjectCapitalizationInput: $updateAscProjectCapitalizationInput
    ) {
      ...AscProject
    }
  }
  ${AscProjectFragmentDocument}
`;

export const GET_ASC_PROJECT_APPROVAL_STATUS = gql`
  query GetAscProjectApprovalStatus($ascProjectId: ID!, $fiscalYearId: ID!, $periodName: PeriodName!) {
    getAscProject(ascProjectId: $ascProjectId, fiscalYearId: $fiscalYearId, periodName: $periodName) {
      id
      approvalStatus(fiscalYearId: $fiscalYearId, periodName: $periodName) {
        ...AscProjectApprovedStatus
      }
    }
  }
  ${AscProjectApprovedStatusFragmentDocument}
`;

export const UPDATE_RD_PROJECT = gql`
  mutation UpdateRdProject($rdProjectId: ID!, $updateRdProjectInput: UpdateRdProjectInput!) {
    updateRdProject(rdProjectId: $rdProjectId, updateRdProjectInput: $updateRdProjectInput) {
      ...RdProject
    }
  }
  ${RdProjectFragmentDocument}
`;

export const MERGE_ASC_PROJECTS = gql`
  mutation MergeAscProjects(
    $companyId: ID!
    $input: MergeAscProjectsInput!
    $fiscalYearId: ID!
    $periodName: PeriodName!
  ) {
    mergeAscProjects(companyId: $companyId, mergeAscProjectsInput: $input) {
      ...AscProject
    }
  }
  ${AscProjectFragmentDocument}
`;

export const UPDATE_ASC_PROJECT_CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE = gql`
  mutation UpdateAscProjectCapitalizableProjectClusteringOverride(
    $companyId: ID!
    $ascProjectId: ID!
    $updateAscProjectCapitalizableProjectClusteringOverrideInput: UpdateAscProjectCapitalizableProjectClusteringOverrideInput!
    $fiscalYearId: ID!
    $periodName: PeriodName!
  ) {
    updateAscProjectCapitalizableProjectClusteringOverride(
      companyId: $companyId
      ascProjectId: $ascProjectId
      updateAscProjectCapitalizableProjectClusteringOverrideInput: $updateAscProjectCapitalizableProjectClusteringOverrideInput
    ) {
      ...AscProject
    }
  }
  ${AscProjectFragmentDocument}
`;

export const BATCH_UPDATE_ASC_PROJECT_CAPITALIZABLE_PROJECT_CLUSTERING_OVERRIDE = gql`
  mutation BatchUpdateAscProjectCapitalizableProjectClusteringOverride(
    $companyId: ID!
    $batchUpdateAscProjectCapitalizableProjectClusteringOverrideInput: BatchUpdateAscProjectCapitalizableProjectClusteringOverrideInput!
    $fiscalYearId: ID!
    $periodName: PeriodName!
  ) {
    batchUpdateAscProjectCapitalizableProjectClusteringOverride(
      companyId: $companyId
      batchUpdateAscProjectCapitalizableProjectClusteringOverrideInput: $batchUpdateAscProjectCapitalizableProjectClusteringOverrideInput
    ) {
      ...AscProject
    }
  }
  ${AscProjectFragmentDocument}
`;

export const MERGE_RD_PROJECTS = gql`
  mutation MergeRdProjects($fiscalYearId: ID!, $input: MergeRdProjectsInput!) {
    mergeRdProjects(fiscalYearId: $fiscalYearId, mergeRdProjectsInput: $input) {
      ... on BackgroundOperation {
        id
        operationName: name
        progressPercent
        createdAt
      }
    }
  }
`;

export const GET_RD_PROJECT_COUNT_FOR_DETAILS_UPDATE = gql`
  query GetRdProjectCountForDetailsUpdate($fiscalYearId: ID!, $scope: RdProjectDetailsUpdateScope!) {
    getRdProjectCountForDetailsUpdate(fiscalYearId: $fiscalYearId, scope: $scope)
  }
`;

export const REGENERATE_RD_PROJECT_DETAILS = gql`
  mutation RegenerateRdProjectDetails($fiscalYearId: ID!, $input: RegenerateRdProjectDetailsInput!) {
    regenerateRdProjectDetails(fiscalYearId: $fiscalYearId, regenerateRdProjectDetailsInput: $input) {
      id
      backgroundOperation {
        id
        progressPercent
      }
    }
  }
`;

/**
 * The following fetched fields will update the affected projects and contributors to ensure consistency
 */
export const MOVE_TICKETS_TO_ASC_PROJECT = gql`
  mutation MoveTicketsToAscProject(
    $ascProjectId: ID!
    $input: MoveTicketsToAscProjectInput!
    $fiscalYearId: ID!
    $periodName: PeriodName!
  ) {
    moveTicketsToAscProject(ascProjectId: $ascProjectId, moveTicketsToAscProjectInput: $input) {
      ascProjects {
        ...AscProject
      }
      contributors {
        ...GetContributor
      }
    }
  }
  ${AscProjectFragmentDocument}
  ${GetContributorFragmentDocument}
`;

/**
 * The following fetched fields will update the affected projects and contributors to ensure consistency
 */
export const MOVE_TICKETS_TO_RD_PROJECT = gql`
  mutation MoveTicketsToRdProject($rdProjectId: ID!, $input: MoveTicketsToRdProjectInput!) {
    moveTicketsToRdProject(rdProjectId: $rdProjectId, moveTicketsToRdProjectInput: $input) {
      rdProjects {
        ...RdProject
      }
    }
  }
  ${RdProjectFragmentDocument}
`;

export const GET_ASC_PROJECT_TP_TICKETS = gql`
  query GetAscProjectTpTickets(
    $ascProjectId: ID!
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $sorting: [AscProjectTpTicketsViewSort!]
    $filter: AscProjectTpTicketsViewFilter
    $paging: CursorPaging
  ) {
    getAscProjectTpTickets(
      ascProjectId: $ascProjectId
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      sorting: $sorting
      filter: $filter
      paging: $paging
    ) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...AscProjectTpTicketsView
        }
        cursor
      }
    }
  }
  ${AscProjectTpTicketsViewFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_RD_PROJECT_TP_TICKETS = gql`
  query GetRdProjectTpTickets(
    $rdProjectId: ID!
    $sorting: [RdProjectTpTicketsViewSort!]
    $filter: RdProjectTpTicketsViewFilter
    $paging: CursorPaging
  ) {
    getRdProjectTpTickets(rdProjectId: $rdProjectId, sorting: $sorting, filter: $filter, paging: $paging) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...RdProjectTpTicketsView
        }
        cursor
      }
    }
  }
  ${RdProjectTpTicketsViewFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_ASC_PROJECT_TEAM_MEMBERS = gql`
  query GetAscProjectTeamMembers(
    $ascProjectId: ID!
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $filter: AscProjectTeamMemberFilter
    $sorting: [AscProjectTeamMemberSort!]
    $paging: CursorPaging
  ) {
    getAscProjectTeamMembers(
      ascProjectId: $ascProjectId
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      filter: $filter
      sorting: $sorting
      paging: $paging
    ) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...AscProjectTeamMember
        }
        cursor
      }
    }
  }
  ${CursorPageInfoFragmentDocument}
  ${AscProjectTeamMemberFragmentDocument}
`;

export const GET_RD_PROJECT_TEAM_MEMBERS = gql`
  query GetRdProjectTeamMembers(
    $rdProjectId: ID!
    $fiscalYearId: ID!
    $filter: RdProjectTeamMemberFilter
    $sorting: [RdProjectTeamMemberSort!]
    $paging: CursorPaging
  ) {
    getRdProjectTeamMembers(
      rdProjectId: $rdProjectId
      fiscalYearId: $fiscalYearId
      filter: $filter
      sorting: $sorting
      paging: $paging
    ) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...RdProjectTeamMember
        }
        cursor
      }
    }
  }
  ${CursorPageInfoFragmentDocument}
  ${RdProjectTeamMemberFragmentDocument}
`;

export const SYNC_PAYROLL_PROVIDER = gql`
  mutation SyncPayrollProvider($fiscalYearId: ID!, $payrollProvider: PayrollProvider!) {
    syncPayrollProvider(fiscalYearId: $fiscalYearId, payrollProvider: $payrollProvider) {
      success
      errors {
        code
        source
        row
        column
        sheet
        value
      }
    }
  }
`;

// This will only get the first PAGINATION_AMOUNT expenses
export const SYNC_ACCOUNTING_PROVIDER = gql`
  mutation SyncAccountingProvider($fiscalYearId: ID!, $provider: String!) {
    syncAccountingProvider(fiscalYearId: $fiscalYearId, provider: $provider) {
      success
    }
  }
`;

export const GET_MERGE_DEV_LINK_TOKEN = gql`
  query GetMergeDevLinkToken($userId: ID!, $input: GetMergeDevLinkTokenInput!) {
    getMergeDevLinkToken(userId: $userId, input: $input)
  }
`;

export const CREATE_MERGE_DEV_ACCESS_TOKEN_FOR_ACCOUNTING = gql`
  mutation CreateMergeDevAccessTokenForAccounting($userId: ID!, $input: CreateMergeDevAccessTokenForAccountingInput!) {
    createMergeDevAccessTokenForAccounting(userId: $userId, input: $input) {
      id
      accountingTokens {
        id
        createdAt
        accountingProvider
        acquisitionMethod
      }
    }
  }
`;

export const CREATE_MERGE_DEV_ACCESS_TOKEN_FOR_TICKETING = gql`
  mutation CreateMergeDevAccessTokenForTicketing($userId: ID!, $input: CreateMergeDevAccessTokenForTicketingInput!) {
    createMergeDevAccessTokenForTicketing(userId: $userId, input: $input) {
      id
      ticketingTokens {
        id
        ticketingProvider
      }
    }
  }
`;

/**
 * The connections list's own query, deliberately not part of GET_COMPANY. Three of these fields are
 * field resolvers — two database queries per connection plus a live Merge probe — and GET_COMPANY is
 * the app-wide company query that some flows poll every second. Only the Data Sources page wants
 * them, so only it asks.
 */
export const GET_TICKETING_CONNECTIONS = gql`
  query GetTicketingConnections($companyId: ID!) {
    getCompany(companyId: $companyId) {
      id
      gitHubAppAvailable
      ticketingTokens {
        id
        ticketingProvider
        acquisitionMethod
        installationStatus
        createdAt
        organization
        pendingScheduledSyncCount
        lastSuccessfulSync {
          at
          acquisitionMethod
        }
        needsRelink
      }
    }
  }
`;

export const DISCONNECT_TICKETING_TOKEN = gql`
  mutation DisconnectTicketingToken($ticketingTokenId: ID!) {
    disconnectTicketingToken(ticketingTokenId: $ticketingTokenId) {
      ticketingTokenId
      scheduledSyncsCancelled
    }
  }
`;

export const CREATE_GITHUB_APP_INSTALL_URL = gql`
  mutation CreateGitHubAppInstallUrl($userId: ID!, $input: CreateGitHubAppInstallUrlInput!) {
    createGitHubAppInstallUrl(userId: $userId, input: $input)
  }
`;

export const CREATE_GITHUB_APP_AUTHORIZATION_URL = gql`
  mutation CreateGitHubAppAuthorizationUrl($userId: ID!, $input: CreateGitHubAppAuthorizationUrlInput!) {
    createGitHubAppAuthorizationUrl(userId: $userId, input: $input)
  }
`;

export const CREATE_FINCH_ACCESS_TOKEN = gql`
  mutation CreateNewFinchAccessToken(
    $userId: ID!
    $companyId: ID!
    $finchAuthCode: String!
    $provider: PayrollProvider!
  ) {
    createFinchAccessToken(userId: $userId, companyId: $companyId, finchAuthCode: $finchAuthCode, provider: $provider) {
      id
      payrollTokens {
        ...PayrollToken
      }
    }
  }
  ${PayrollTokenFragmentDocument}
`;

export const PROVISION_EMAIL_PASSWORD_USER = gql`
  mutation ProvisionEmailPasswordUser($email: String!, $fullName: String!, $password: String!, $continueUrl: String) {
    provisionEmailPasswordUser(
      input: { email: $email, fullName: $fullName, password: $password, continueUrl: $continueUrl }
    )
  }
`;

export const ONBOARD_USER = gql`
  mutation OnboardUser($userId: ID!, $fullName: String!, $companyName: String!) {
    onboardUser(userId: $userId, onboardUserInput: { fullName: $fullName, companyName: $companyName }) {
      ...User
      companies {
        ...Company
      }
    }
  }
  ${UserFragmentDocument}
  ${CompanyFragmentDocument}
  ${OrganizationFragmentDocument}
`;

export const ADD_ORGANIZATION_INVITATION = gql`
  mutation AddOrganizationInvitation(
    $organizationId: ID!
    $addOrganizationInvitationInput: AddOrganizationInvitationInput!
  ) {
    addOrganizationInvitation(
      organizationId: $organizationId
      addOrganizationInvitationInput: $addOrganizationInvitationInput
    ) {
      id
      email
      scope
      expiresAt
      revokedAt
      acceptedAt
      derivedState
    }
  }
`;

export const DELETE_ORGANIZATION_INVITATION = gql`
  mutation DeleteOrganizationInvitation($organizationInvitationId: ID!) {
    deleteOrganizationInvitation(organizationInvitationId: $organizationInvitationId) {
      id
    }
  }
`;

export const ACCEPT_INVITATION = gql`
  mutation AcceptInvitation($acceptInvitationInput: AcceptInvitationInput!) {
    acceptInvitation(acceptInvitationInput: $acceptInvitationInput) {
      id
      organizationId
    }
  }
`;

export const RESEND_ORGANIZATION_INVITATION = gql`
  mutation ResendOrganizationInvitation($organizationInvitationId: ID!) {
    resendOrganizationInvitation(organizationInvitationId: $organizationInvitationId) {
      id
    }
  }
`;

export const RESEND_MY_ORGANIZATION_INVITATION = gql`
  mutation ResendMyOrganizationInvitation($organizationInvitationId: ID!) {
    resendMyOrganizationInvitation(organizationInvitationId: $organizationInvitationId) {
      id
    }
  }
`;

export const REMOVE_USER_ACCESS_FROM_ORGANIZATION = gql`
  mutation RemoveUserAccessFromOrganization($organizationId: ID!, $userId: ID!) {
    removeUserAccessFromOrganization(organizationId: $organizationId, userId: $userId)
  }
`;

export const REPLACE_USER_ACCESS_IN_ORGANIZATION = gql`
  mutation UpdateUserAccessInOrganization(
    $organizationId: ID!
    $userId: ID!
    $replaceUserAccessInOrganizationInput: ReplaceUserAccessInOrganizationInput!
  ) {
    replaceUserAccessInOrganization(
      organizationId: $organizationId
      userId: $userId
      replaceUserAccessInOrganizationInput: $replaceUserAccessInOrganizationInput
    )
  }
`;

export const CREATE_COMPANY_FOR_ORGANIZATION = gql`
  mutation CreateCompanyForOrganization(
    $organizationId: ID!
    $createCompanyForOrganizationInput: CreateCompanyForOrganizationInput!
  ) {
    createCompanyForOrganization(
      organizationId: $organizationId
      createCompanyForOrganizationInput: $createCompanyForOrganizationInput
    ) {
      ...Company
      members {
        ...Member
      }
    }
  }
  ${CompanyFragmentDocument}
  ${MemberFragmentDocument}
`;

export const DELETE_COMPANY = gql`
  mutation DeleteCompany($companyId: ID!) {
    deleteCompany(companyId: $companyId)
  }
`;

export const GET_COMPANY = gql`
  query GetCompany($companyId: ID!) {
    getCompany(companyId: $companyId) {
      ...Company
      businessQuestions {
        ...BusinessQuestions
      }
      priorYearQREs {
        year
        amount
        taxYearStartDate
        taxYearEndDate
      }
      accountingTokens {
        id
        createdAt
        accountingProvider
        acquisitionMethod
      }
      payrollTokens {
        ...PayrollToken
      }
      ticketingTokens {
        id
        ticketingProvider
        acquisitionMethod
        installationStatus
        organization
      }
      syncedTicketingProviders
      gitHubAppAvailable
      bulkTicketingDataUploads {
        ...BulkTicketingDataFileUpload
      }
      bulkPriorYearTaxDocumentUploads {
        ...FileUpload
      }
      syncedPayrollMonthExpense {
        ...SyncedPayrollMonthExpense
      }
      timesheetUploads {
        ...FileUpload
      }
    }
  }
  ${BusinessQuestionsFragmentDocument}
  ${CompanyFragmentDocument}
  ${BulkTicketingDataFileUploadFragmentDocument}
  ${FileUploadFragmentDocument}
  ${PayrollTokenFragmentDocument}
  ${SyncedPayrollMonthExpenseFragmentDocument}
`;

/** Kept out of GET_COMPANY so only companies that sync GitHub pay for it. Callers skip it otherwise. */
export const GET_GITHUB_EMAIL_MAPPING_DATA = gql`
  query GetGithubEmailMappingData($companyId: ID!) {
    getCompany(companyId: $companyId) {
      id
      githubEmailMappingUploads {
        ...FileUpload
      }
    }
  }
  ${FileUploadFragmentDocument}
`;

export const GET_COMPANIES = gql`
  query GetCompaniesForUser($userId: ID!) {
    getCompaniesForUser(userId: $userId) {
      ...Company
      members {
        ...Member
      }
      invitedMembers {
        ...CompanyInvitation
      }
      fiscalYears {
        id
        rdTaxCredit {
          id
          lastUpdatedAt
        }
      }
    }
  }
  ${CompanyFragmentDocument}
  ${MemberFragmentDocument}
  ${CompanyInvitationFragmentDocument}
`;

export const GET_RD_EXPENSE = gql`
  query GetRdExpense($rdExpenseId: ID!) {
    getRdExpense(rdExpenseId: $rdExpenseId) {
      ...GetRdExpense
    }
  }
  ${GetRdExpenseFragmentDocument}
`;

export const GET_RD_EXPENSE_PROJECT_ALLOCATION_REPORT = gql`
  query GetRdExpenseProjectAllocationReport($fiscalYearId: ID!, $providerType: ProviderType!) {
    getRdExpenseProjectAllocationReport(fiscalYearId: $fiscalYearId, providerType: $providerType) {
      signedUrl
    }
  }
`;

export const GET_ASC_WAGE_REPORT = gql`
  query GetAscWageReport($fiscalYearId: ID!, $periodName: PeriodName!) {
    getAscWageReport(fiscalYearId: $fiscalYearId, periodName: $periodName) {
      signedUrl
    }
  }
`;

export const UPLOAD_ASC_WAGE_REPORT = gql`
  mutation UploadAscWageReport($fiscalYearId: ID!, $periodName: PeriodName!, $file: Upload!) {
    uploadAscWageReport(fiscalYearId: $fiscalYearId, periodName: $periodName, file: $file) {
      errors {
        type
        row
        column
        value
      }
    }
  }
`;

export const UPLOAD_RD_EXPENSE_PROJECT_ALLOCATION_REPORT = gql`
  mutation UploadRdExpenseProjectAllocationReport(
    $fiscalYearId: ID!
    $uploadRdExpenseProjectAllocationReportInput: UploadRdExpenseProjectAllocationReportInput!
    $file: Upload!
  ) {
    uploadRdExpenseProjectAllocationReport(
      fiscalYearId: $fiscalYearId
      uploadRdExpenseProjectAllocationReportInput: $uploadRdExpenseProjectAllocationReportInput
      file: $file
    ) {
      errors {
        type
        row
        column
        value
      }
    }
  }
`;

export const UPLOAD_PAYROLL_EXPENSE = gql`
  mutation UploadPayrollExpenseCsv($fiscalYearId: ID!, $payrollProvider: PayrollProvider!, $file: Upload!) {
    uploadPayrollExpenseCsv(fiscalYearId: $fiscalYearId, payrollProvider: $payrollProvider, file: $file) {
      payrollQres {
        id
      }
      errors {
        source
        code
        row
        column
        value
      }
    }
  }
`;

export const UPLOAD_ACCOUNTING_EXPENSE = gql`
  mutation UploadAccountingExpenseCsv($fiscalYearId: ID!, $accountingProvider: AccountingProvider!, $file: Upload!) {
    uploadAccountingExpenseCsv(fiscalYearId: $fiscalYearId, accountingProvider: $accountingProvider, file: $file) {
      accountingQres {
        id
      }
      errors {
        source
        code
        row
        column
        value
      }
    }
  }
`;

export const IS_VERSION_OUTDATED = gql`
  query IsVersionOutdated {
    isVersionOutdated
  }
`;

export const GET_SYNCED_PROVIDERS = gql`
  query GetSyncedProviders($fiscalYearId: ID!) {
    getFiscalYear(fiscalYearId: $fiscalYearId) {
      id
      syncedProviders {
        ...SyncedProviders
      }
    }
  }
  ${SyncedProvidersFragmentDocument}
`;

export const UPDATE_ACCOUNTING_EXPENSE = gql`
  mutation UpdateAccountingExpense(
    $accountingExpenseId: ID!
    $updateAccountingExpenseInput: UpdateAccountingExpenseInput!
  ) {
    updateAccountingExpense(
      accountingExpenseId: $accountingExpenseId
      updateAccountingExpenseInput: $updateAccountingExpenseInput
    ) {
      ...GetAccountingExpense
    }
  }
  ${GetAccountingExpenseFragmentDocument}
`;

export const DELETE_ACCOUNTING_TOKENS_BY_COMPANY_AND_PROVIDER = gql`
  mutation DeleteAccountingTokensByCompanyAndProvider($companyId: ID!, $accountingProvider: AccountingProvider!) {
    deleteAccountingTokensByCompanyAndProvider(companyId: $companyId, accountingProvider: $accountingProvider) {
      ids
    }
  }
`;

export const DELETE_PAYROLL_TOKENS_BY_COMPANY_AND_PROVIDER = gql`
  mutation DeletePayrollTokensByCompanyAndProvider($companyId: ID!, $payrollProvider: PayrollProvider!) {
    deletePayrollTokensByCompanyAndProvider(companyId: $companyId, payrollProvider: $payrollProvider) {
      ids
    }
  }
`;

export const GET_CONTRIBUTOR = gql`
  query getContributor($contributorId: ID!, $fiscalYearId: ID!, $periodName: PeriodName!) {
    getContributor(contributorId: $contributorId) {
      ...GetContributor
    }
  }
  ${GetContributorFragmentDocument}
`;

export const COMBINE_CONTRIBUTOR = gql`
  mutation CombineContributor($companyId: ID!, $input: CombineContributorInput!) {
    combineContributor(companyId: $companyId, input: $input) {
      ...Contributor
    }
  }
  ${ContributorFragmentDocument}
`;

export const UPDATE_CONTRIBUTOR_ALLOCATIONS_FOR_ASC = gql`
  mutation UpdateContributorAllocationsForAsc(
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $input: UpdateContributorAllocationForAscInput!
  ) {
    updateContributorAllocationsForAsc(fiscalYearId: $fiscalYearId, periodName: $periodName, input: $input)
  }
`;

export const GENERATE_ASC_REPORT = gql`
  mutation GenerateAscReport($fiscalYearId: ID!, $periodName: PeriodName!) {
    generateAscReport(fiscalYearId: $fiscalYearId, periodName: $periodName) {
      id
    }
  }
`;

export const GENERATE_RD_PROJECTS_REPORT = gql`
  mutation GenerateRdProjectsReport($fiscalYearId: ID!) {
    generateRdProjectsReport(fiscalYearId: $fiscalYearId) {
      id
    }
  }
`;

export const GET_AND_SAVE_RD_PROJECTS_REPORT_SIGNED_URL = gql`
  mutation GetAndSaveRdProjectsReportSignedUrl($fiscalYearId: ID!) {
    getAndSaveRdProjectsReportSignedUrl(fiscalYearId: $fiscalYearId) {
      signedUrl
    }
  }
`;

export const GET_AND_SAVE_ASC_REPORT_SIGNED_URL = gql`
  mutation GetAndSaveAscReportSignedUrl($fiscalYearId: ID!, $periodName: PeriodName!) {
    getAndSaveAscReportSignedUrl(fiscalYearId: $fiscalYearId, periodName: $periodName) {
      signedUrl
    }
  }
`;

export const GET_COMPANY_QUESTIONS = gql`
  query GetCompanyQuestions($companyId: ID!) {
    getCompany(companyId: $companyId) {
      ...Company
      businessQuestions {
        ...BusinessQuestions
      }
      questionSections {
        ...QuestionSection
      }
    }
  }
  ${CompanyFragmentDocument}
  ${BusinessQuestionsFragmentDocument}
  ${QuestionSectionFragmentDocument}
`;

export const UPDATE_FISCAL_YEAR_PROCESSING_TICKETING_DATA_STATE = gql`
  mutation UpdateFiscalYearProcessingTicketingDataState(
    $fiscalYearId: ID!
    $processingTicketingDataState: ProcessingTicketingDataState!
  ) {
    updateFiscalYearProcessingTicketingDataState(
      fiscalYearId: $fiscalYearId
      processingTicketingDataState: $processingTicketingDataState
    ) {
      id
      processingTicketingDataState
    }
  }
`;

export const GET_GAAP_ONBOARDING_FLOW_STATUS = gql`
  query GetGaapOnboardingFlowStatus($companyId: ID!) {
    getGaapOnboardingFlowStatus(companyId: $companyId) {
      percentCompleted
    }
  }
`;

export const GET_DOCUMENTS = gql`
  query GetDocuments($fiscalYearId: ID!) {
    getDocuments(fiscalYearId: $fiscalYearId) {
      id
      federalRdTaxCreditDocuments {
        ...FederalRdTaxCreditDocuments
      }
      stateRdTaxCreditDocuments {
        ...StateRdTaxCreditDocument
      }
    }
  }
  ${FederalRdTaxCreditDocumentsFragmentDocument}
  ${StateRdTaxCreditDocumentFragmentDocument}
`;

export const GET_FEDERAL_DOCUMENT_STATUSES = gql`
  query GetFederalDocumentStatuses($fiscalYearId: ID!) {
    getFederalDocumentStatuses(fiscalYearId: $fiscalYearId) {
      taxCreditFormStatus
      taxWriteUpStatus
    }
  }
`;
export const GET_LATEST_FEDERAL_DOCUMENT_STATUSES_FOR_COMPANY = gql`
  query GetLatestFederalDocumentStatusesForCompany($companyId: ID!) {
    getLatestFederalDocumentStatusesForCompany(companyId: $companyId) {
      taxCreditFormStatus
      taxWriteUpStatus
    }
  }
`;

export const GET_STATE_DOCUMENT_STATUS = gql`
  query GetStateDocumentStatus($fiscalYearId: ID!) {
    getStateDocumentStatus(fiscalYearId: $fiscalYearId)
  }
`;

export const GET_ALL_TAX_DOCUMENTS = gql`
  query GetAllDocuments($companyId: ID!) {
    getAllDocuments(companyId: $companyId) {
      id
      federalRdTaxCreditDocuments {
        ...FederalRdTaxCreditDocuments
      }
      stateRdTaxCreditDocuments {
        ...StateRdTaxCreditDocument
      }
    }
  }
  ${FederalRdTaxCreditDocumentsFragmentDocument}
  ${StateRdTaxCreditDocumentFragmentDocument}
`;

export const GET_QUESTION_SECTIONS = gql`
  query GetQuestionSections($companyId: ID!) {
    getCompany(companyId: $companyId) {
      id
      questionSections {
        ...QuestionSection
      }
    }
  }
  ${QuestionSectionFragmentDocument}
`;

export const UPDATE_COMPANY_QUESTION = gql`
  mutation UpdateCompanyQuestion($companyId: ID!, $companyQuestionInput: CompanyQuestionInput!) {
    updateCompanyQuestion(companyId: $companyId, companyQuestionInput: $companyQuestionInput) {
      id
    }
  }
`;

export const CREATE_FISCAL_YEAR = gql`
  mutation CreateFiscalYear($companyId: ID!, $createFiscalYearInput: CreateFiscalYearInput!) {
    createFiscalYear(companyId: $companyId, createFiscalYearInput: $createFiscalYearInput) {
      ...FiscalYear
    }
  }
  ${FiscalYearFragmentDocument}
`;

export const UPDATE_PAYROLL_EXPENSE = gql`
  mutation UpdatePayrollExpense($payrollExpenseId: ID!, $input: UpdatePayrollExpenseInput!) {
    updatePayrollExpense(payrollExpenseId: $payrollExpenseId, input: $input) {
      ...PayrollQre
    }
  }
  ${PayrollQreFragmentDocument}
`;

export const UPDATE_RD_EXPENSE_PROJECT_ALLOCATIONS = gql`
  mutation UpdateRdExpenseProjectAllocations(
    $rdExpenseId: ID!
    $updateRdExpenseProjectAllocationsInput: UpdateRdExpenseProjectAllocationsInput!
  ) {
    updateRdExpenseProjectAllocations(
      rdExpenseId: $rdExpenseId
      updateRdExpenseProjectAllocationsInput: $updateRdExpenseProjectAllocationsInput
    ) {
      ...GetRdExpense
    }
  }
  ${GetRdExpenseFragmentDocument}
`;

export const APPLY_WEIGHTED_RD_PERCENTAGE_TO_EXPENSES = gql`
  mutation ApplyWeightedRdPercentageToExpenses(
    $fiscalYearId: ID!
    $applyWeightedRdPercentageToExpensesInput: ApplyWeightedRdPercentageToExpensesInput!
    $filter: PayrollQreFilter
  ) {
    applyWeightedRdPercentageToExpenses(
      fiscalYearId: $fiscalYearId
      applyWeightedRdPercentageToExpensesInput: $applyWeightedRdPercentageToExpensesInput
      filter: $filter
    ) {
      ...ApplyWeightedRdPercentageToExpensesResult
    }
  }
  ${ApplyWeightedRdPercentageToExpensesResultFragmentDocument}
`;

export const GET_ALL_FISCAL_YEARS = gql`
  query GetAllFiscalYears($companyId: ID!) {
    getCompany(companyId: $companyId) {
      id
      fiscalYears {
        ...FiscalYear
      }
    }
  }
  ${FiscalYearFragmentDocument}
`;

export const GET_ACCOUNTING_EXPENSE = gql`
  query GetAccountingExpense($accountingExpenseId: ID!) {
    getAccountingExpense(accountingExpenseId: $accountingExpenseId) {
      ...GetAccountingExpense
    }
  }
  ${GetAccountingExpenseFragmentDocument}
`;

export const GET_ACCOUNTING_QRES = gql`
  query GetAccountingQres(
    $fiscalYearId: ID!
    $sorting: [AccountingQreSort!]
    $filter: AccountingQreFilter
    $paging: CursorPaging
  ) {
    getAccountingQres(fiscalYearId: $fiscalYearId, sorting: $sorting, filter: $filter, paging: $paging) {
      # IMPORTANT: components that use this query must use useFragment with AccountingQreFragment
      # If you change this query, make sure to update the fragment
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...AccountingQre
        }
        cursor
      }
    }
  }
  ${AccountingQreFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_PAYROLL_EXPENSE = gql`
  query GetPayrollExpense($payrollExpenseId: ID!) {
    getPayrollExpense(payrollExpenseId: $payrollExpenseId) {
      ...GetPayrollExpense
    }
  }
  ${GetPayrollExpenseFragmentDocument}
`;

export const GET_RD_EXPENSE_PROJECT_ALLOCATIONS = gql`
  query GetRdExpenseProjectAllocations(
    $rdExpenseId: ID!
    $fiscalYearId: ID!
    $filter: RdExpenseProjectAllocationFilter! = {}
    $paging: CursorPaging! = { first: 100 }
    $sorting: [RdExpenseProjectAllocationSort!]! = []
  ) {
    getRdExpenseProjectAllocations(
      rdExpenseId: $rdExpenseId
      fiscalYearId: $fiscalYearId
      filter: $filter
      paging: $paging
      sorting: $sorting
    ) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...RdExpenseProjectAllocation
        }
        cursor
      }
    }
  }
  ${RdExpenseProjectAllocationFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_ASC_CONTRIBUTOR_PROJECT_ALLOCATIONS = gql`
  query GetAscContributorProjectAllocations(
    $contributorId: ID!
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $filter: AscContributorProjectAllocationFilter! = {}
    $paging: CursorPaging! = { first: 100 }
    $sorting: [AscContributorProjectAllocationSort!]! = []
  ) {
    getAscContributorProjectAllocations(
      contributorId: $contributorId
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      filter: $filter
      paging: $paging
      sorting: $sorting
    ) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...AscContributorProjectAllocation
        }
        cursor
      }
    }
  }
  ${AscContributorProjectAllocationFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_PAYROLL_QRES = gql`
  query GetPayrollQres(
    $fiscalYearId: ID!
    $sorting: [PayrollQreSort!]
    $filter: PayrollQreFilter
    $paging: CursorPaging
  ) {
    getPayrollQres(fiscalYearId: $fiscalYearId, sorting: $sorting, filter: $filter, paging: $paging) {
      # IMPORTANT: components that use this query must use useFragment with PayrollQreFragment
      # If you change this query, make sure to update the fragment
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...PayrollQre
        }
        cursor
      }
    }
  }
  ${PayrollQreFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_FISCAL_YEAR = gql`
  query GetFiscalYear($fiscalYearId: ID!) {
    getFiscalYear(fiscalYearId: $fiscalYearId) {
      id
      year
      taxYearStartDate
      taxYearEndDate
      lockedForCustomers
      productType
      enabledDatetime
      companyId
      processingTicketingDataState
      syncedProviders {
        ...SyncedProviders
      }
      syncedRdPayrollCost {
        ...SyncedRdPayrollCost
      }
      rdTaxCredit {
        id
        lastUpdatedAt
      }
      sredTaxCredit {
        id
      }
    }
  }
  ${SyncedProvidersFragmentDocument}
  ${SyncedRdPayrollCostFragmentDocument}
`;

export const GET_TICKETING_USERS = gql`
  query GetTicketingUsers(
    $sorting: [TicketingUserSort!]
    $filter: TicketingUserFilter
    $companyId: ID!
    $rdTaxCreditId: ID!
    $paging: CursorPaging
  ) {
    getTicketingUsers(
      sorting: $sorting
      filter: $filter
      paging: $paging
      companyId: $companyId
      rdTaxCreditId: $rdTaxCreditId
    ) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...TicketingUser
        }
        cursor
      }
    }
  }
  ${TicketingUserFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const CREATE_ASC_PROJECT = gql`
  mutation CreateAscProject(
    $companyId: ID!
    $createAscProjectInput: CreateAscProjectInput!
    $fiscalYearId: ID!
    $periodName: PeriodName!
  ) {
    createAscProject(companyId: $companyId, createAscProjectInput: $createAscProjectInput) {
      ...AscProject
    }
  }
  ${AscProjectFragmentDocument}
`;

export const CREATE_RD_PROJECT = gql`
  mutation CreateRdProject($fiscalYearId: ID!, $createRdProjectInput: CreateRdProjectInput!) {
    createRdProject(fiscalYearId: $fiscalYearId, createRdProjectInput: $createRdProjectInput) {
      ...RdProject
    }
  }
  ${RdProjectFragmentDocument}
`;

export const DELETE_ASC_PROJECT = gql`
  mutation DeleteAscProject($ascProjectId: ID!) {
    deleteAscProject(ascProjectId: $ascProjectId) {
      success
    }
  }
`;

export const GET_ASC_PERIOD_LOCK_STATUS = gql`
  query GetAscPeriodLockStatus($fiscalYearId: ID!, $periodName: PeriodName!) {
    getAscPeriodLockStatus(fiscalYearId: $fiscalYearId, periodName: $periodName) {
      ...AscPeriodLockStatus
    }
  }
  ${AscPeriodLockStatusFragmentDocument}
`;

export const SET_ASC_PERIOD_LOCK = gql`
  mutation SetAscPeriodLock($fiscalYearId: ID!, $periodName: PeriodName!, $isLocked: Boolean!) {
    setAscPeriodLock(fiscalYearId: $fiscalYearId, periodName: $periodName, isLocked: $isLocked) {
      ...AscPeriodLockStatus
    }
  }
  ${AscPeriodLockStatusFragmentDocument}
`;

export const SET_ASC_PROJECT_APPROVAL = gql`
  mutation SetAscProjectApproval(
    $ascProjectId: ID!
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $setAscProjectApprovalInput: SetAscProjectApprovalInput!
  ) {
    setAscProjectApproval(
      ascProjectId: $ascProjectId
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      setAscProjectApprovalInput: $setAscProjectApprovalInput
    ) {
      id
      approvalStatus(fiscalYearId: $fiscalYearId, periodName: $periodName) {
        ...AscProjectApprovedStatus
      }
    }
  }
  ${AscProjectApprovedStatusFragmentDocument}
`;

export const CLEAR_ASC_PROJECT_APPROVAL = gql`
  mutation ClearAscProjectApproval($ascProjectId: ID!, $fiscalYearId: ID!, $periodName: PeriodName!) {
    clearAscProjectApproval(ascProjectId: $ascProjectId, fiscalYearId: $fiscalYearId, periodName: $periodName) {
      id
      approvalStatus(fiscalYearId: $fiscalYearId, periodName: $periodName) {
        ...AscProjectApprovedStatus
      }
    }
  }
  ${AscProjectApprovedStatusFragmentDocument}
`;

export const DELETE_RD_PROJECT = gql`
  mutation DeleteRdProject($rdProjectId: ID!) {
    deleteRdProject(rdProjectId: $rdProjectId) {
      success
    }
  }
`;

export const UPDATE_RD_TAX_CREDIT_OVERRIDES = gql`
  mutation UpdateRdTaxCreditOverrides($fiscalYearId: ID!, $rdTaxCreditOverrides: RdTaxCreditOverridesInput!) {
    updateRdTaxCreditOverrides(fiscalYearId: $fiscalYearId, rdTaxCreditOverrides: $rdTaxCreditOverrides) {
      id
      filingMethod
      isReducedCredit
    }
  }
`;

export const GET_CREDIT_ESTIMATE = gql`
  query GetCreditEstimate($fiscalYearId: ID!) {
    getCreditEstimate(fiscalYearId: $fiscalYearId) {
      ... on Form6765Values {
        id
        isEstimateCalculation
        ascMethodCreditCents
        regularMethodCreditCents
        wagesQreCents
        suppliesQreCents
        cloudQreCents
        contractorQreCents
        totalQreCents
      }
      ... on MissingValue {
        reason
      }
    }
  }
`;

export const ALLOW_REAL_COMPANY = gql`
  mutation AllowRealCompany($userId: ID!) {
    allowRealCompany(userId: $userId) {
      ...User
    }
  }
  ${UserFragmentDocument}
  ${OrganizationFragmentDocument}
`;

export const CREATE_CONTRIBUTOR = gql`
  mutation CreateContributor($companyId: ID!, $input: CreateContributorInput!) {
    createContributor(companyId: $companyId, input: $input) {
      ...Contributor
    }
  }
  ${ContributorFragmentDocument}
`;

export const SEND_EMAIL_VERIFICATION = gql`
  mutation SendEmailVerification($userId: ID!, $continueUrl: String) {
    sendEmailVerification(userId: $userId, continueUrl: $continueUrl)
  }
`;

export const SEND_MAGIC_LINK_EMAIL = gql`
  mutation SendMagicLinkEmail($sendMagicLinkEmailInput: SendMagicLinkEmailInput!) {
    sendMagicLinkEmail(sendMagicLinkEmailInput: $sendMagicLinkEmailInput)
  }
`;

export const SEND_RESET_PASSWORD_EMAIL = gql`
  mutation SendResetPasswordEmail($sendResetPasswordEmailInput: SendResetPasswordEmailInput!) {
    sendResetPasswordEmail(sendResetPasswordEmailInput: $sendResetPasswordEmailInput)
  }
`;

export const DELETE_ACCOUNTING_SYNC_EVENT_FOR_FISCAL_YEAR = gql`
  mutation deleteAccountingSyncEventForFiscalYear($fiscalYearId: ID!, $accountingProviderSyncEventId: ID!) {
    deleteAccountingSyncEventForFiscalYear(
      fiscalYearId: $fiscalYearId
      accountingProviderSyncEventId: $accountingProviderSyncEventId
    ) {
      deletedAccountingProviderSyncEventId
    }
  }
`;

export const DELETE_PAYROLL_SYNC_EVENT_FOR_FISCAL_YEAR = gql`
  mutation deletePayrollSyncEventForFiscalYear($fiscalYearId: ID!, $payrollProviderSyncEventId: ID!) {
    deletePayrollSyncEventForFiscalYear(
      fiscalYearId: $fiscalYearId
      payrollProviderSyncEventId: $payrollProviderSyncEventId
    ) {
      deletedPayrollProviderSyncEventId
    }
  }
`;

export const DELETE_PAYROLL_MONTH_EXPENSE_SYNC_EVENT = gql`
  mutation DeletePayrollMonthExpenseSyncEvent($companyId: ID!, $payrollMonthExpenseSyncEventId: ID!) {
    deletePayrollMonthExpenseSyncEvent(
      companyId: $companyId
      payrollMonthExpenseSyncEventId: $payrollMonthExpenseSyncEventId
    ) {
      deletedPayrollMonthExpenseSyncEventId
    }
  }
`;

export const GET_RD_PROJECT_SUPPORTING_DOCUMENT_SIGNED_UPLOAD_URL = gql`
  mutation GetRdProjectSupportingDocumentSignedUploadUrl($rdProjectId: ID!, $fileName: String!) {
    getRdProjectSupportingDocumentSignedUploadUrl(rdProjectId: $rdProjectId, fileName: $fileName) {
      signedUrl
      permanentUrl
    }
  }
`;

export const ADD_RD_PROJECT_SUPPORTING_DOCUMENT = gql`
  mutation AddRdProjectSupportingDocument($rdProjectId: ID!, $input: AddRdProjectSupportingDocumentInput!) {
    addRdProjectSupportingDocument(rdProjectId: $rdProjectId, addRdProjectSupportingDocumentInput: $input) {
      id
      processingState
      supportingDocuments {
        ...FileUpload
      }
    }
  }
  ${FileUploadFragmentDocument}
`;

export const REMOVE_RD_PROJECT_SUPPORTING_DOCUMENT = gql`
  mutation RemoveRdProjectSupportingDocument($fileUploadId: ID!) {
    removeRdProjectSupportingDocument(fileUploadId: $fileUploadId) {
      id
      processingState
      supportingDocuments {
        ...FileUpload
      }
    }
  }
  ${FileUploadFragmentDocument}
`;

export const GENERATE_RD_DETAILS = gql`
  mutation GenerateRdDetails($rdProjectId: ID!, $fiscalYearId: ID!, $input: GenerateRdDetailsInput!) {
    generateRdDetails(rdProjectId: $rdProjectId, fiscalYearId: $fiscalYearId, generateRdDetailsInput: $input) {
      id
      name
      description
      processingState
      technicalChallenges
      processOfExperimentation
      rdDetermination {
        ...ProjectRdDetermination
      }
    }
  }
  ${ProjectRdDeterminationFragmentDocument}
`;

export const UPDATE_RD_PROJECT_ELIGIBILITY = gql`
  mutation UpdateRdProjectEligibility(
    $rdProjectId: ID!
    $rdProjectEligibilityAnswersInput: RdProjectEligibilityAnswersInput!
  ) {
    updateRdProjectEligibility(
      rdProjectId: $rdProjectId
      rdProjectEligibilityAnswersInput: $rdProjectEligibilityAnswersInput
    ) {
      ...RdProject
    }
  }

  ${RdProjectFragmentDocument}
`;

export const GENERATE_ELEVATOR_PITCH = gql`
  mutation GenerateElevatorPitch($companyId: ID!) {
    generateElevatorPitch(companyId: $companyId) {
      ...Question
    }
  }
  ${QuestionFragmentDocument}
`;

export const GET_TICKETING_DATA_SIGNED_UPLOAD_URL = gql`
  mutation GetTicketingDataSignedUploadUrl($companyId: ID!, $fileName: String!, $action: UploadAction!) {
    getTicketingDataSignedUploadUrl(companyId: $companyId, fileName: $fileName, action: $action) {
      signedUrl
      permanentUrl
    }
  }
`;

export const GET_PRIOR_YEAR_TAX_DOCUMENT_SIGNED_UPLOAD_URL = gql`
  mutation GetPriorYearTaxDocumentSignedUploadUrl($companyId: ID!, $fileName: String!) {
    getPriorYearTaxDocumentSignedUploadUrl(companyId: $companyId, fileName: $fileName) {
      signedUrl
      permanentUrl
    }
  }
`;

export const ADD_COMPANY_BULK_TICKETING_DATA_FILE = gql`
  mutation AddCompanyBulkTicketingDataFile($companyId: ID!, $input: AddCompanyBulkTicketingDataInput!) {
    addCompanyBulkTicketingDataFile(companyId: $companyId, addCompanyBulkTicketingDataInput: $input) {
      id
      bulkTicketingDataUploads {
        ...BulkTicketingDataFileUpload
      }
    }
  }
  ${BulkTicketingDataFileUploadFragmentDocument}
`;

export const REMOVE_COMPANY_BULK_TICKETING_DATA_FILE = gql`
  mutation RemoveCompanyBulkTicketingDataFile($fileUploadId: ID!) {
    removeCompanyBulkTicketingDataFile(fileUploadId: $fileUploadId) {
      id
      bulkTicketingDataUploads {
        ...BulkTicketingDataFileUpload
      }
    }
  }
  ${BulkTicketingDataFileUploadFragmentDocument}
`;

export const ADD_PRIOR_YEAR_TAX_DOCUMENT = gql`
  mutation AddPriorYearTaxDocument($companyId: ID!, $input: AddPriorYearTaxDocumentInput!) {
    addPriorYearTaxDocument(companyId: $companyId, addPriorYearTaxDocumentInput: $input) {
      id
      bulkPriorYearTaxDocumentUploads {
        ...FileUpload
      }
    }
  }
  ${FileUploadFragmentDocument}
`;

export const REMOVE_PRIOR_YEAR_TAX_DOCUMENT = gql`
  mutation RemovePriorYearTaxDocument($fileUploadId: ID!) {
    removePriorYearTaxDocument(fileUploadId: $fileUploadId) {
      id
      bulkPriorYearTaxDocumentUploads {
        ...FileUpload
      }
    }
  }
  ${FileUploadFragmentDocument}
`;

export const GET_ASC_WAGES = gql`
  query GetAscWages(
    $sorting: [AscWageSort!]
    $filter: AscWageFilter
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $paging: CursorPaging
  ) {
    getAscWages(
      sorting: $sorting
      filter: $filter
      paging: $paging
      fiscalYearId: $fiscalYearId
      periodName: $periodName
    ) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...AscWage
        }
        cursor
      }
    }
  }
  ${AscWageFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_ASC_WAGE = gql`
  query GetAscWage($ascWageId: ID!, $fiscalYearId: ID!, $periodName: PeriodName!) {
    getAscWage(ascWageId: $ascWageId, fiscalYearId: $fiscalYearId, periodName: $periodName) {
      ...AscWage
    }
  }
  ${AscWageFragmentDocument}
`;

export const GET_PAYROLL_QRE_SUMMARY = gql`
  query GetPayrollQreSummary($fiscalYearId: ID!, $filter: PayrollQreSummaryFilter) {
    getPayrollQreSummary(fiscalYearId: $fiscalYearId, filter: $filter) {
      avg {
        qreRdPercentage
      }
      count {
        name
      }
      sum {
        amountCents
        qualifiedAmountCents
      }
      uniqueCount {
        costCenter
        country
        department
        entity
        managerName
        rdCategory
        state
        title
      }
    }
  }
`;

export const GET_SRED_PAYROLL_EXPENSES = gql`
  query GetSredPayrollExpenses(
    $sredTaxCreditId: ID!
    $sorting: [SredPayrollExpenseSort!]
    $filter: SredPayrollExpenseFilter
    $paging: CursorPaging
  ) {
    getSredPayrollExpenses(sredTaxCreditId: $sredTaxCreditId, sorting: $sorting, filter: $filter, paging: $paging) {
      pageInfo {
        ...CursorPageInfo
      }
      edges {
        node {
          ...SredPayrollExpense
        }
        cursor
      }
    }
  }
  ${SredPayrollExpenseFragmentDocument}
  ${CursorPageInfoFragmentDocument}
`;

export const GET_SRED_PAYROLL_EXPENSES_SUMMARY = gql`
  query GetSredPayrollExpensesSummary($sredTaxCreditId: ID!, $filter: SredPayrollExpensesSummaryFilter) {
    getSredPayrollExpensesSummary(sredTaxCreditId: $sredTaxCreditId, filter: $filter) {
      avg {
        sredEligiblePercentage
      }
      count {
        name
      }
      sum {
        amountCents
        qualifiedAmountCents
      }
      uniqueCount {
        costCenter
        department
        entity
        managerName
        province
        sredCategory
        title
      }
    }
  }
`;

export const GET_ACCOUNTING_QRE_SUMMARY = gql`
  query GetAccountingQreSummary($fiscalYearId: ID!, $filter: AccountingQreSummaryFilter) {
    getAccountingQreSummary(fiscalYearId: $fiscalYearId, filter: $filter) {
      avg {
        qreRdPercentage
      }
      count {
        name
      }
      sum {
        amountCents
        qualifiedAmountCents
      }
      uniqueCount {
        account
        costCenter
        country
        department
        entity
        rdCategory
        state
      }
    }
  }
`;

export const GET_ASC_WAGE_SUMMARY = gql`
  query GetAscWageSummary($fiscalYearId: ID!, $periodName: PeriodName!, $filter: AscWageSummaryFilter) {
    getAscWageSummary(fiscalYearId: $fiscalYearId, periodName: $periodName, filter: $filter) {
      avg {
        allocationPercent
      }
      count {
        name
      }
      sum {
        capitalizedTotalCompensationCents
        salaryCompensationCents
        stockCompensationCents
        totalCompensationCents
      }
    }
  }
`;

export const GET_ASC_PROJECTS_SUMMARY = gql`
  query GetAscProjectsSummary(
    $companyId: ID!
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $filter: AscProjectsSummaryFilter
  ) {
    getAscProjectsSummary(
      companyId: $companyId
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      filter: $filter
    ) {
      count {
        name
      }
    }
  }
`;

export const GET_BACKGROUND_OPERATIONS = gql`
  query GetBackgroundOperations($companyId: ID!) {
    getBackgroundOperations(companyId: $companyId) {
      id
      createdAt
      name
      progressPercent
    }
  }
`;

export const UPDATE_SHORT_YEAR_PERIOD = gql`
  mutation updateShortYearPeriod($companyId: ID!, $shortYearPeriod: MonthYearPeriodInput!) {
    updateShortYearPeriod(companyId: $companyId, shortYearPeriod: $shortYearPeriod) {
      id
      priorYearRevenues {
        id
        revenueCents
        year
        taxYearStartDate
        taxYearEndDate
        revenueSource
      }
      priorYearQREs {
        year
        amount
        taxYearStartDate
        taxYearEndDate
      }
    }
  }
`;

export const UPDATE_PRIOR_YEAR_QRE = gql`
  mutation UpdatePriorYearQre($companyId: ID!, $taxYearStartDate: String!, $amountCents: BigInt!) {
    updatePriorYearQre(companyId: $companyId, taxYearStartDate: $taxYearStartDate, amountCents: $amountCents) {
      id
      priorYearQREs {
        year
        amount
        taxYearStartDate
        taxYearEndDate
      }
    }
  }
`;

export const UPDATE_PRIOR_YEAR_REVENUE = gql`
  mutation UpdatePriorYearRevenue($priorYearRevenueId: ID!, $revenueCents: BigInt!) {
    updatePriorYearRevenue(priorYearRevenueId: $priorYearRevenueId, revenueCents: $revenueCents) {
      id
      revenueCents
      year
      taxYearStartDate
      taxYearEndDate
      revenueSource
    }
  }
`;

export const GET_PRODUCTS = gql`
  query GetProducts($companyId: ID!) {
    getProducts(companyId: $companyId) {
      ...Product
    }
  }
  ${ProductFragmentDocument}
`;

export const CREATE_PRODUCT = gql`
  mutation CreateProduct($companyId: ID!, $createProductInput: CreateProductInput!) {
    createProduct(companyId: $companyId, createProductInput: $createProductInput) {
      ...Product
    }
  }
  ${ProductFragmentDocument}
`;

export const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($productId: ID!, $updateProductInput: UpdateProductInput!) {
    updateProduct(productId: $productId, updateProductInput: $updateProductInput) {
      ...Product
    }
  }
  ${ProductFragmentDocument}
`;

export const DELETE_PRODUCT = gql`
  mutation DeleteProduct($productId: ID!) {
    deleteProduct(productId: $productId) {
      id
    }
  }
`;

export const GET_FINCH_SESSION = gql`
  query GetFinchSession($companyId: ID!, $provider: String!) {
    getFinchSession(companyId: $companyId, provider: $provider) {
      sessionId
    }
  }
`;

export const GET_COMPANY_OFFICER_PAYROLL_QRES = gql`
  query GetCompanyOfficerPayrollQres($fiscalYearId: ID!) {
    getCompanyOfficerPayrollQres(fiscalYearId: $fiscalYearId) {
      ...PayrollQre
    }
  }
  ${PayrollQreFragmentDocument}
`;

export const SEARCH_FOR_POTENTIAL_COMPANY_OFFICERS = gql`
  query SearchForPotentialCompanyOfficers($fiscalYearId: ID!, $searchTerm: String!) {
    searchForPotentialCompanyOfficers(fiscalYearId: $fiscalYearId, searchTerm: $searchTerm) {
      ...PayrollQre
    }
  }
  ${PayrollQreFragmentDocument}
`;

export const UPDATE_COMPANY_OFFICER = gql`
  mutation UpdateCompanyOfficer($payrollExpenseId: ID!, $input: UpdateCompanyOfficerInput!) {
    updateCompanyOfficer(payrollExpenseId: $payrollExpenseId, input: $input) {
      ...PayrollQre
    }
  }
  ${PayrollQreFragmentDocument}
`;

export const GET_UNFINISHED_SYNC_EVENTS_IN_PERIOD_BY_ANALYSIS_TYPE = gql`
  query GetUnfinishedSyncEventsInPeriodByAnalysisType(
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $analysisType: AnalysisType!
  ) {
    getUnfinishedSyncEventsInPeriodByAnalysisType(
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      analysisType: $analysisType
    ) {
      startMonthYear {
        month
        year
      }
      endMonthYear {
        month
        year
      }
    }
  }
`;

export const START_PROCESSING_TICKETING_DATA = gql`
  mutation StartProcessingTicketingData(
    $companyId: ID!
    $ticketingTokenId: ID!
    $input: StartProcessingTicketingDataInput!
  ) {
    startProcessingTicketingData(companyId: $companyId, ticketingTokenId: $ticketingTokenId, input: $input) {
      ...Company
    }
  }
  ${CompanyFragmentDocument}
`;

export const GET_ONE_SCHEMA_JWT = gql`
  mutation GetOneSchemaJwt($userId: ID!) {
    getOneSchemaJwt(userId: $userId)
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($userId: ID!, $input: UpdateUserInput!) {
    updateUser(userId: $userId, input: $input) {
      id
      fullName
      email
      phone
    }
  }
`;

export const GET_TIMESHEET_FILE_SIGNED_UPLOAD_URL = gql`
  mutation GetTimesheetFileSignedUploadUrl($companyId: ID!, $effortUnit: TimesheetEffortUnit!, $name: String!) {
    getTimesheetFileSignedUploadUrl(companyId: $companyId, effortUnit: $effortUnit, name: $name) {
      signedUrl
      permanentUrl
    }
  }
`;

export const ADD_TIMESHEET_FILE = gql`
  mutation AddTimesheetFile($companyId: ID!, $addTimesheetFileInput: AddTimesheetFileInput!) {
    addTimesheetFile(companyId: $companyId, addTimesheetFileInput: $addTimesheetFileInput) {
      ...Company
    }
  }
  ${CompanyFragmentDocument}
`;

export const GET_ASC_COST_SIGNED_UPLOAD_URL = gql`
  mutation GetAscCostSignedUploadUrl($companyId: ID!, $fileName: String!) {
    getAscCostSignedUploadUrl(companyId: $companyId, fileName: $fileName) {
      signedUrl
      permanentUrl
    }
  }
`;

export const GET_RD_PAYROLL_COST_SIGNED_UPLOAD_URL = gql`
  mutation GetRdPayrollCostSignedUploadUrl($companyId: ID!, $fileName: String!) {
    getRdPayrollCostSignedUploadUrl(companyId: $companyId, fileName: $fileName) {
      signedUrl
      permanentUrl
    }
  }
`;

export const ADD_RD_PAYROLL_COST_FILE = gql`
  mutation AddRdPayrollCostFile($fiscalYearId: ID!, $addRdPayrollCostFileInput: AddRdPayrollCostFileInput!) {
    addRdPayrollCostFile(fiscalYearId: $fiscalYearId, addRdPayrollCostFileInput: $addRdPayrollCostFileInput) {
      ...Company
    }
  }
  ${CompanyFragmentDocument}
`;

export const ADD_ASC_COST_FILE = gql`
  mutation AddAscCostFile($fiscalYearId: ID!, $periodName: PeriodName!, $addAscCostFileInput: AddAscCostFileInput!) {
    addAscCostFile(fiscalYearId: $fiscalYearId, periodName: $periodName, addAscCostFileInput: $addAscCostFileInput) {
      ...Company
    }
  }
  ${CompanyFragmentDocument}
`;

export const GET_ASC_PROJECTS_FILE_SIGNED_UPLOAD_URL = gql`
  mutation GetAscProjectsFileSignedUploadUrl($companyId: ID!, $fileName: String!) {
    getAscProjectsFileSignedUploadUrl(companyId: $companyId, fileName: $fileName) {
      signedUrl
      permanentUrl
    }
  }
`;

export const ADD_ASC_PROJECTS_FILE = gql`
  mutation AddAscProjectsFile($companyId: ID!, $input: AddAscProjectsFileInput!) {
    addAscProjectsFile(companyId: $companyId, input: $input) {
      ...Company
    }
  }
  ${CompanyFragmentDocument}
`;

export const GET_RD_PROJECTS_FILE_SIGNED_UPLOAD_URL = gql`
  mutation GetRdProjectsFileSignedUploadUrl($fiscalYearId: ID!, $fileName: String!) {
    getRdProjectsFileSignedUploadUrl(fiscalYearId: $fiscalYearId, fileName: $fileName) {
      signedUrl
      permanentUrl
    }
  }
`;

export const ADD_RD_PROJECTS_FILE = gql`
  mutation AddRdProjectsFile($fiscalYearId: ID!, $input: AddRdProjectsFileInput!) {
    addRdProjectsFile(fiscalYearId: $fiscalYearId, input: $input) {
      ...Company
    }
  }
  ${CompanyFragmentDocument}
`;

export const GET_GITHUB_EMAIL_MAPPING_SIGNED_UPLOAD_URL = gql`
  mutation GetGithubEmailMappingSignedUploadUrl($companyId: ID!) {
    getGithubEmailMappingSignedUploadUrl(companyId: $companyId) {
      signedUrl
      permanentUrl
    }
  }
`;

export const ADD_GITHUB_EMAIL_MAPPING_FILE = gql`
  mutation AddGithubEmailMappingFile($companyId: ID!, $input: AddGithubEmailMappingFileInput!) {
    addGithubEmailMappingFile(companyId: $companyId, input: $input) {
      ...Company
    }
  }
  ${CompanyFragmentDocument}
`;

export const GET_ASC_PROJECT_GROUPING_TICKETS_OR_COLLECTIONS = gql`
  query GetAscProjectGroupingTicketsOrCollections(
    $ascProjectId: ID!
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $searchKeyword: String
  ) {
    getAscProjectGroupingTicketsOrCollections(
      ascProjectId: $ascProjectId
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      searchKeyword: $searchKeyword
    ) {
      ...AscGroupingTicketOrCollection
    }
  }
  ${AscGroupingTicketOrCollectionFragmentDocument}
`;

export const GET_RD_PROJECT_GROUPING_TICKETS_OR_COLLECTIONS = gql`
  query GetRdProjectGroupingTicketsOrCollections($rdProjectId: ID!, $fiscalYearId: ID!, $searchKeyword: String) {
    getRdProjectGroupingTicketsOrCollections(
      rdProjectId: $rdProjectId
      fiscalYearId: $fiscalYearId
      searchKeyword: $searchKeyword
    ) {
      ...RdGroupingTicketOrCollection
    }
  }
  ${RdGroupingTicketOrCollectionFragmentDocument}
`;

export const GET_MERGED_RD_PROJECT_ID = gql`
  query GetMergedRdProjectId($fiscalYearId: ID!, $backgroundOperationId: ID!) {
    getMergedRdProjectId(fiscalYearId: $fiscalYearId, backgroundOperationId: $backgroundOperationId)
  }
`;

export const GET_ASC_SNAPSHOT_DOCUMENT_EVENTS = gql`
  query GetAscSnapshotDocumentEvents($fiscalYearId: ID!) {
    getAscSnapshotDocumentEvents(fiscalYearId: $fiscalYearId) {
      ...DocumentEvent
    }
  }
  ${DocumentEventFragmentDocument}
`;

export const GET_LATEST_ASC_REPORT_DOCUMENT_EVENT = gql`
  query GetLatestAscReportDocumentEvent($fiscalYearId: ID!, $periodName: PeriodName!) {
    getLatestAscReportDocumentEvent(fiscalYearId: $fiscalYearId, periodName: $periodName) {
      ...DocumentEvent
      createdAt
    }
  }
  ${DocumentEventFragmentDocument}
`;

export const GET_DOCUMENT_EVENT_SIGNED_URL = gql`
  query GetDocumentEventSignedUrl($documentEventId: ID!) {
    getDocumentEventSignedUrl(documentEventId: $documentEventId)
  }
`;

export const APPLY_WEIGHTED_CAPITALIZED_PERCENTAGE_TO_CONTRIBUTORS = gql`
  mutation ApplyWeightedCapitalizedPercentageToContributors(
    $fiscalYearId: ID!
    $periodName: PeriodName!
    $input: ApplyWeightedCapitalizedPercentageToContributorInput!
  ) {
    applyWeightedCapitalizedPercentageToContributors(
      fiscalYearId: $fiscalYearId
      periodName: $periodName
      input: $input
    ) {
      ...ApplyWeightedCapitalizedPercentToContributorsResult
    }
  }
  ${ApplyWeightedCapitalizedPercentToContributorsResultFragmentDocument}
`;

export const GET_RD_TAX_CREDIT_CONTRIBUTOR = gql`
  query getRdTaxCreditContributor($rdTaxCreditContributorId: ID!) {
    getRdTaxCreditContributor(rdTaxCreditContributorId: $rdTaxCreditContributorId) {
      ...GetRdTaxCreditContributor
    }
  }
  ${GetRdTaxCreditContributorFragmentDocument}
`;

export const LINK_TICKETING_PROVIDER_USER_TO_RD_TAX_CREDIT_CONTRIBUTOR = gql`
  mutation LinkTicketingProviderUserToRdTaxCreditContributor(
    $rdTaxCreditContributorId: ID!
    $ticketingProviderUserId: ID!
  ) {
    linkTicketingProviderUserToPayrollExpense(
      rdTaxCreditContributorId: $rdTaxCreditContributorId
      ticketingProviderUserId: $ticketingProviderUserId
    ) {
      ...GetRdTaxCreditContributor
    }
  }
  ${GetRdTaxCreditContributorFragmentDocument}
`;

export const UNLINK_TICKETING_PROVIDER_USER_FROM_RD_TAX_CREDIT_CONTRIBUTOR = gql`
  mutation UnlinkTicketingProviderUserFromRdTaxCreditContributor(
    $rdTaxCreditContributorId: ID!
    $ticketingProviderUserId: ID!
  ) {
    unlinkTicketingProviderUserFromRdTaxCreditContributor(
      rdTaxCreditContributorId: $rdTaxCreditContributorId
      ticketingProviderUserId: $ticketingProviderUserId
    ) {
      ...GetRdTaxCreditContributor
    }
  }
  ${GetRdTaxCreditContributorFragmentDocument}
`;

export const GET_SUPERVISOR_RD_ANALYSIS_PREVIEW = gql`
  query GetSupervisorRdAnalysisPreview($fiscalYearId: ID!, $expenseIds: [ID!], $filter: PayrollQreFilter) {
    getSupervisorRdAnalysisPreview(fiscalYearId: $fiscalYearId, expenseIds: $expenseIds, filter: $filter) {
      columnNames
      resultCsv
    }
  }
`;

export const GET_SUPERVISORS_IN_EXPENSES = gql`
  query GetSupervisorsInExpenses($fiscalYearId: ID!, $expenseIds: [ID!]!) {
    getSupervisorsInExpenses(fiscalYearId: $fiscalYearId, expenseIds: $expenseIds) {
      supervisorIds
      supervisorCount
      nonSupervisorIds
    }
  }
`;

export const APPLY_SUPERVISOR_RD_PERCENTAGE = gql`
  mutation ApplySupervisorRdPercentage(
    $fiscalYearId: ID!
    $expenseIds: [ID!]
    $applyToAll: Boolean
    $filter: PayrollQreFilter
  ) {
    applySupervisorRdPercentage(
      fiscalYearId: $fiscalYearId
      expenseIds: $expenseIds
      applyToAll: $applyToAll
      filter: $filter
    ) {
      id
      createdAt
      name
      progressPercent
    }
  }
`;

export const GET_SINGLE_TENANT_REDIRECT_URL = gql`
  query GetSingleTenantRedirectUrl($email: String!) {
    getSingleTenantRedirectUrl(email: $email)
  }
`;
